export const M6_UNIT_1 = {
  id: "m6-u1-animals",
  title: "Unit 1: Animals Vocabulary",
  topic: "Master animal vocabulary to talk about wildlife, pets, nature, and travel experiences!",
  iconType: "paw",
  grammarImage: "/grammar/grammar-m6-u1.jpg",
  grammarText: "Animal vocabulary with irregular plurals, key animal differences (turtle/tortoise, frog/toad, rabbit/hare), and real-world contexts for nature conversations.",

  sets: [

    // ===================================================================
    // SET 1: COMMON MAMMALS — PETS & FARM (A2)
    // ===================================================================
    {
      id: "m6-u1-s1",
      title: "Set 1: Common Mammals",
      context: `*Maya's neighborhood — Saturday morning*

Maya is 16 years old and loves nature. Today she is visiting a pet shop with her friend Leo.
"I want a dog," says Maya. "But my mom prefers cats."
Leo smiles. "I have a rabbit and two hamsters at home!"
Later, they drive past a farm. Maya sees cows, horses, and pigs in the fields.
"I love all animals," she says.

*El barrio de Maya — sábado por la mañana*

Maya tiene 16 años y le encanta la naturaleza. Hoy visita una tienda de mascotas con su amigo Leo.
"Quiero un perro", dice Maya. "Pero mi mamá prefiere los gatos."
Leo sonríe. "¡En casa tengo un conejo y dos hámsteres!"
Más tarde, pasan junto a una granja. Maya ve vacas, caballos y cerdos en los campos.
"Me encantan todos los animales", dice.`,

      questions: [
        {
          type: "match",
          q: "Match the animal to its description:",
          pairs: [
            { left: "Dog", right: "Common pet, barks" },
            { left: "Cat", right: "Common pet, meows" },
            { left: "Cow", right: "Farm animal, gives milk" }
          ],
          a: "completed",
          successTip: "Dogs and cats = most popular pets worldwide 🐕🐈. Cows are essential farm animals found on every continent!"
        },
        {
          type: "mc",
          q: "Maya sees a female animal on the farm that gives milk. It is a ___.",
          options: ["cow", "bull", "ox"],
          a: "cow",
          tip: "COW = female! BULL = male! This is a very common mistake for Spanish speakers.",
          successTip: "Cow = female 🐄, Bull = male 🐂. Very important distinction in English!"
        },
        {
          type: "input",
          q: "Leo's favorite pet says 'meow' and has soft fur. It is a ___.",
          a: "cat",
          alts: ["Cat","Kitty", "Kitten"],
          tip: "The most popular pet in many countries!"
        },
        {
          type: "mc",
          q: "Maya wants a pet that barks and is very loyal. She wants a ___.",
          options: ["dog", "cat", "rabbit"],
          a: "dog",
          successTip: "Over 470 million pet dogs worldwide 🐕. The most popular pet in the USA, UK, Canada, and Australia!"
        },
        {
          type: "order",
          q: "Translate: 'Tengo un perro.'",
          words: ["I", "have", "a", "dog."],
          a: "I have a dog.",
          extra: ["has", "an", "dogs."],
          tip: "Always use 'a' before a singular animal: a dog, a cat, a rabbit.",
          successTip: "In English-speaking countries, talking about pets is very common small talk! 🐾"
        },
        {
          type: "match",
          q: "Match the farm animals to the sound they make:",
          pairs: [
            { left: "Pig", right: "Says 'oink'" },
            { left: "Horse", right: "Says 'neigh'" },
            { left: "Sheep", right: "Says 'baa'" }
          ],
          a: "completed",
          successTip: "Farm animal sounds are great conversation starters! 🚜"
        },
        {
          type: "input",
          q: "Leo sees a farm animal with soft white wool that says 'baa'. It is a ___.",
          a: "sheep",
          alts: ["Sheep"],
          successTip: "New Zealand has 5 sheep for every person 🐑. Australia and the UK are also famous for sheep farming!"
        },
        {
          type: "mc",
          q: "At the farm, Maya sees an animal that people can ride. It is a ___.",
          options: ["horse", "cow", "pig"],
          a: "horse",
          successTip: "Horses were the main form of transportation for thousands of years 🐴. Still popular for sports and recreation today!"
        },
        {
          type: "input",
          q: "A pink farm animal that says 'oink' is a ___.",
          a: "pig",
          alts: ["Pig"],
          tip: "Pigs are actually very intelligent animals!"
        },
        {
          type: "order",
          q: "Translate: Mi gato es muy bonito.",
          words: ["My", "cat", "is", "very", "cute."],
          a: "My cat is very cute.",
          extra: ["cats", "are", "hungry."],
          successTip: "Native speakers love talking about their cute pets 😍. Great conversation starter anywhere!"
        }
      ]
    },

    // ===================================================================
    // SET 2: PLURALS & SMALL MAMMALS (A2)
    // ===================================================================
    {
      id: "m6-u1-s2",
      title: "Set 2: Plurals & Small Mammals",
      context: `*Maya's school — Monday, English class*

Maya's teacher, Mr. Ellis, is teaching animal vocabulary.
"Pay attention to irregular plurals," he says. "Some animals don't follow the normal rules."
He writes on the board: mouse → mice. One sheep, two sheep. One deer, two deer.
Maya writes everything in her notebook.
"English is strange sometimes," whispers Leo.
"But interesting!" Maya replies with a smile.

*El colegio de Maya — lunes, clase de inglés*

El profesor de Maya, el Sr. Ellis, está enseñando vocabulario de animales.
"Presten atención a los plurales irregulares", dice. "Algunos animales no siguen las reglas normales."
Escribe en la pizarra: mouse → mice. One sheep, two sheep. One deer, two deer.
Maya escribe todo en su cuaderno.
"A veces el inglés es extraño", susurra Leo.
"¡Pero interesante!", responde Maya con una sonrisa.`,

      questions: [
        {
          type: "mc",
          q: "Mr. Ellis writes on the board: 'The plural of mouse is ___.'",
          options: ["mice", "mouses", "mouse"],
          a: "mice",
          tip: "IRREGULAR! Never add -S! Mouse → Mice.",
          successTip: "One mouse 🐭, two mice 🐭🐭. Irregular plural from Old English. Learn it by heart!"
        },
        {
          type: "order",
          q: "Translate: El plural de ratón es ratones.",
          words: ["The", "plural", "of", "mouse", "is", "mice."],
          a: "The plural of mouse is mice.",
          extra: ["rat", "are", "mickey."],
          tip: "IRREGULAR PLURAL! Mouse → Mice (not mouses!)",
          successTip: "Mouse → Mice is one of the most common irregular plurals in English! 📚"
        },
        {
          type: "input",
          q: "A small brown or gray animal with a long tail that lives in houses is a ___.",
          a: "mouse",
          alts: ["Mouse", "mice","rat","hamster","rabbit"],
          tip: "Smaller than a rat. Very common in houses."
        },
        {
          type: "mc",
          q: "Maya sees a bigger animal in the corner. It is larger than a mouse and people do not like them in houses. It is a ___.",
          options: ["rat", "mouse", "hamster"],
          a: "rat",
          successTip: "Rat = bigger and wild 🐀. Mouse = smaller 🐭. Both are rodents!"
        },
        {
          type: "match",
          q: "Mr. Ellis writes three examples on the board. Match the singular to the plural:",
          pairs: [
            { left: "One sheep", right: "Two sheep" },
            { left: "One deer", right: "Two deer" },
            { left: "One fish", right: "Two fish" }
          ],
          a: "completed",
          tip: "SAME FORM! Sheep, deer, fish = same in singular and plural!",
          successTip: "Sheep, deer, fish = no change! 🐑🦌🐟. Very common mistake for learners of English!"
        },
        {
          type: "input",
          q: "Maya saw one deer yesterday. Today she saw three ___.",
          a: "deer",
          alts: ["Deer"],
          tip: "SAME FORM! One deer, two deer, three deer — never 'deers'!"
        },
        {
          type: "mc",
          q: "Leo asks: 'What is the plural of sheep?' The answer is ___.",
          options: ["sheep", "sheeps", "sheepes"],
          a: "sheep",
          tip: "NO CHANGE! One sheep, two sheep — never 'sheeps'!",
          successTip: "Singular = plural 🐑. Very important for farming vocabulary!"
        },
        {
          type: "order",
          q: "Translate: Vi cinco peces en el acuario.",
          words: ["I", "saw", "five", "fish", "in", "the", "aquarium."],
          a: "I saw five fish in the aquarium.",
          extra: ["fishes", "an", "sea."],
          tip: "FISH = same in singular and plural! Five fish — never 'fishes'!"
        },
        {
          type: "input",
          q: "Leo's small pet at home. It runs in a wheel at night. It is a ___.",
          a: "hamster",
          alts: ["Hamster"],
          successTip: "Hamsters are popular pets for children worldwide 🐹. Easy to care for!"
        },
        {
          type: "mc",
          q: "A small furry animal with long ears that hops is a ___.",
          options: ["rabbit", "mouse", "rat"],
          a: "rabbit",
          successTip: "Rabbits are common pets and wild animals 🐰. Australia has millions of wild rabbits!"
        }
      ]
    },

    // ===================================================================
    // SET 3: BIRDS (A2)
    // ===================================================================
    {
      id: "m6-u1-s3",
      title: "Set 3: Birds",
      context: `*A park near the river — Saturday afternoon*

Maya and Leo go birdwatching with Maya's uncle, Carlos.
He has binoculars and a bird guide book.
"Look at that large white bird on the river," says Carlos. "See its very long neck? That is a swan."
"And that big one flying high in the sky?" asks Maya.
"That is an eagle," says Carlos. "The national bird of the USA."
Leo points at a small yellow bird in a cage at the café near the entrance.
"That one is a canary," says Maya. "People keep them as pets."

*Un parque cerca del río — sábado por la tarde*

Maya y Leo van a observar aves con el tío de Maya, Carlos.
Tiene binoculares y una guía de aves.
"Mira ese pájaro grande y blanco en el río", dice Carlos. "¿Ves su cuello muy largo? Eso es un cisne."
"¿Y ese grande que vuela muy alto en el cielo?", pregunta Maya.
"Eso es un águila", dice Carlos. "El ave nacional de los EE.UU."
Leo señala un pajarito amarillo en una jaula del café cerca de la entrada.
"Ese es un canario", dice Maya. "La gente los tiene como mascotas."`,

      questions: [
        {
          type: "match",
          q: "Match the birds to their descriptions:",
          pairs: [
            { left: "Eagle", right: "Large bird of prey, hunts animals" },
            { left: "Parrot", right: "Colorful bird that learns to talk" },
            { left: "Chicken", right: "Farm bird, gives eggs" }
          ],
          a: "completed",
          successTip: "Over 10,000 bird species worldwide 🦅🦜🐔. Birds live on every continent!"
        },
        {
          type: "mc",
          q: "Carlos points at a very large bird flying high in the sky. It hunts other animals. It is an ___.",
          options: ["eagle", "chicken", "duck"],
          a: "eagle",
          successTip: "The bald eagle is the national bird of the USA 🦅. The golden eagle is found in North America and Europe!"
        },
        {
          type: "input",
          q: "A farm bird that gives eggs and says 'cluck' is a ___.",
          a: "chicken",
          alts: ["Chicken", "hen", "Hen"],
          successTip: "Chickens are the most common farm bird globally 🐔. There are over 25 billion chickens in the world!"
        },
        {
          type: "mc",
          q: "Leo opens the bird book: 'The plural of goose is ___.'",
          options: ["geese", "gooses", "goose"],
          a: "geese",
          tip: "IRREGULAR PLURAL! Never add -S! Goose → Geese.",
          successTip: "One goose 🦢, two geese 🦢🦢. Another irregular plural from Old English!"
        },
        {
          type: "order",
          q: "Translate: El plural de ganso es gansos.",
          words: ["The", "plural", "of", "goose", "is", "geese."],
          a: "The plural of goose is geese.",
          tip: "IRREGULAR! Goose → Geese (not gooses!)"
        },
        {
          type: "input",
          q: "Carlos shows Maya a large white bird on the river. It has a very long neck and moves gracefully. It is a ___.",
          a: "swan",
          alts: ["Swan","goose","geese"],
          tip: "Swans are large white water birds with a long, elegant neck. Different from ducks and geese.",
          successTip: "Swans are famous for their elegance 🦢. A group of swans is called a bevy!"
        },
        {
          type: "mc",
          q: "At the zoo, Maya sees a colorful tropical bird that repeats words she says. It is a ___.",
          options: ["parrot", "eagle", "chicken"],
          a: "parrot",
          successTip: "Parrots can live 50+ years 🦜. Found in tropical regions worldwide!"
        },
        {
          type: "match",
          q: "Match the water birds:",
          pairs: [
            { left: "Duck", right: "Small, says 'quack'" },
            { left: "Swan", right: "Large, long neck, white" },
            { left: "Goose", right: "Bigger than a duck, says 'honk'" }
          ],
          a: "completed",
          successTip: "Ducks, swans, and geese are all water birds! 🦆🦢. Very common in parks and rivers."
        },
        {
          type: "input",
          q: "The small yellow bird that Leo saw in the cage at the café. People keep it as a pet. It is a ___.",
          a: "canary",
          alts: ["Canary","bird"],
          successTip: "Canaries are popular pet birds 🐤. Originally from the Canary Islands — that is where they got their name!"
        },
        {
          type: "order",
          q: "Translate: Vi dos águilas en las montañas.",
          words: ["I", "saw", "two", "eagles", "in", "the", "mountains."],
          a: "I saw two eagles in the mountains.",
          extra: ["see", "read", "sea"],
          successTip: "Birdwatching is a popular hobby worldwide 🦅. Great vocabulary for nature conversations!"
        }
      ]
    },

    // ===================================================================
    // SET 4: REPTILES & AMPHIBIANS (A2+)
    // ===================================================================
    {
      id: "m6-u1-s4",
      title: "Set 4: Reptiles & Amphibians",
      context: `*Maya's living room — Sunday evening*

Maya and Leo are watching a nature documentary called 'Cold-Blooded World'.
The narrator explains: "Reptiles and amphibians look similar, but they are very different animals."
"What is the difference between a turtle and a tortoise?" asks Leo.
Maya reads from her animal guide: "A turtle lives in water. A tortoise lives on land."
"And frogs and toads?"
"A frog has smooth, wet skin. A toad has dry, rough skin."
Leo is impressed. "You know everything about animals, Maya!"

*La sala de Maya — domingo por la tarde*

Maya y Leo están viendo un documental llamado 'Cold-Blooded World'.
El narrador explica: "Los reptiles y los anfibios parecen similares, pero son animales muy diferentes."
"¿Cuál es la diferencia entre 'turtle' y 'tortoise'?", pregunta Leo.
Maya lee de su guía: "Una turtle vive en el agua. Una tortoise vive en tierra."
"¿Y las ranas y los sapos?"
"Una frog tiene la piel suave y húmeda. Un toad tiene la piel seca y rugosa."
Leo está impresionado. "¡Sabes todo sobre los animales, Maya!"`,

      questions: [
        {
          type: "mc",
          q: "The documentary says: 'A turtle ___ in water. A tortoise ___ on land.'",
          options: ["lives / lives", "live / live", "lives / live"],
          a: "lives / lives",
          tip: "THIRD PERSON SINGULAR! Use 'lives' (with S) for he/she/it. Both 'a turtle' and 'a tortoise' are singular!",
          successTip: "Turtle = lives in WATER 🌊. Tortoise = lives on LAND 🏜️. You learned both grammar AND animal facts!"
        },
        {
          type: "match",
          q: "Match the reptiles to their habitats:",
          pairs: [
            { left: "Turtle", right: "Ocean and rivers" },
            { left: "Tortoise", right: "Land only" },
            { left: "Crocodile", right: "Rivers and swamps" }
          ],
          a: "completed",
          successTip: "Knowing where animals live is essential for nature conversations 🦎. Very common in documentaries!"
        },
        {
          type: "input",
          q: "A reptile with a hard shell that swims in the ocean is a ___.",
          a: "turtle",
          alts: ["Turtle", "sea turtle", "Sea turtle"],
          successTip: "Sea turtles can live for over 100 years! 🐢"
        },
        {
          type: "mc",
          q: "The documentary shows a large dangerous reptile with big teeth that lives in rivers in Africa and Australia. It is a ___.",
          options: ["crocodile", "lizard", "snake"],
          a: "crocodile",
          successTip: "Crocodiles are found in Africa, Australia, and the Americas 🐊. They can grow up to 6 meters long!"
        },
        {
          type: "order",
          q: "Translate: Una rana tiene piel suave y un sapo tiene piel rugosa.",
          words: ["A", "frog", "has", "smooth", "skin", "and", "a", "toad", "has", "rough", "skin."],
          a: "A frog has smooth skin and a toad has rough skin.",
          tip: "HAS = third person singular. A frog has... a toad has...",
          successTip: "Frog = smooth, wet skin, lives near water 🐸. Toad = dry, bumpy skin, lives on land. Another animal difference mastered!"
        },
        {
          type: "input",
          q: "A small green animal that jumps and says 'ribbit' is a ___.",
          a: "frog",
          alts: ["Frog","toad"],
          successTip: "Frogs are amphibians, not reptiles! They need water to survive 🐸."
        },
        {
          type: "mc",
          q: "Leo sees a long reptile with no legs in the documentary. It is a ___.",
          options: ["snake", "lizard", "crocodile"],
          a: "snake",
          successTip: "Over 3,000 snake species worldwide 🐍. Found on every continent except Antarctica!"
        },
        {
          type: "match",
          q: "Match the animals to their key features:",
          pairs: [
            { left: "Snake", right: "No legs, reptile" },
            { left: "Lizard", right: "Four legs, reptile" },
            { left: "Frog", right: "Jumps, amphibian" }
          ],
          a: "completed",
          successTip: "Reptiles = snakes, lizards, turtles, crocodiles 🦎. Amphibians = frogs, toads 🐸."
        },
        {
          type: "input",
          q: "A small reptile with four legs that runs fast on walls and rocks is a ___.",
          a: "lizard",
          alts: ["Lizard"],
          successTip: "There are over 6,000 species of lizards worldwide! 🦎"
        },
        {
          type: "mc",
          q: "The narrator asks: 'Which animal is NOT a reptile?'",
          options: ["Frog", "Snake", "Turtle"],
          a: "Frog",
          tip: "FROG = amphibian! Reptiles = snake, turtle, lizard, crocodile.",
          successTip: "Amphibians live in water AND on land 🐸. Reptiles are cold-blooded but are a separate group. Science vocabulary!"
        }
      ]
    },

    // ===================================================================
    // SET 5: SEA CREATURES (A2+)
    // ===================================================================
    {
      id: "m6-u1-s5",
      title: "Set 5: Sea Creatures",
      context: `*City aquarium — Wednesday afternoon*

Maya's wildlife club visits the city aquarium.
"Welcome," says the guide. "Today you will see over 500 sea creatures."
Maya stops in front of a huge tank. She sees sharks, dolphins, and colorful fish.
"That is a blue whale skeleton," says the guide, pointing at the ceiling. "The largest animal that has ever lived on Earth."
Later, they visit a smaller tank with crabs, octopuses, and shrimp.
"In the USA we call them shrimp," says the guide. "In the UK and Australia, people say 'prawn' for the larger ones."

*Acuario de la ciudad — miércoles por la tarde*

El club de naturaleza de Maya visita el acuario de la ciudad.
"Bienvenidos", dice el guía. "Hoy verán más de 500 criaturas marinas."
Maya se detiene frente a un enorme tanque. Ve tiburones, delfines y peces de colores.
"Ese es el esqueleto de una ballena azul", dice el guía, señalando el techo. "El animal más grande que ha existido en la Tierra."
Más tarde, visitan un tanque más pequeño con cangrejos, pulpos y camarones.
"En EE.UU. los llamamos 'shrimp'", dice el guía. "En el Reino Unido y Australia se dice 'prawn' para los más grandes."`,

      questions: [
        {
          type: "match",
          q: "Match the sea animals to their descriptions:",
          pairs: [
            { left: "Shark", right: "Dangerous predator, sharp teeth" },
            { left: "Dolphin", right: "Intelligent and friendly" },
            { left: "Whale", right: "The largest animal on Earth" }
          ],
          a: "completed",
          successTip: "The blue whale is the largest animal ever known 🐋. It can weigh up to 200 tons!"
        },
        {
          type: "mc",
          q: "Maya reads the sign: 'The blue ___ is the largest animal on Earth.'",
          options: ["whale", "shark", "dolphin"],
          a: "whale",
          successTip: "Whale watching is a popular tourist activity 🐋. Common in Canada, Australia, Iceland, and the USA!"
        },
        {
          type: "input",
          q: "A large dangerous fish with sharp teeth and grey skin is a ___.",
          a: "shark",
          alts: ["Shark"],
          successTip: "Over 500 shark species exist 🦈. Most are not dangerous to humans!"
        },
        {
          type: "mc",
          q: "Maya watches a very intelligent sea animal jumping and playing near boats. It is a ___.",
          options: ["dolphin", "shark", "whale"],
          a: "dolphin",
          successTip: "Dolphins are one of the smartest animals on Earth 🐬. Found in oceans worldwide!"
        },
        {
          type: "order",
          q: "Translate: Cual es la diferencia entre camarones y langostinos?",
          words: ["What", "is", "the", "difference", "between", "shrimp", "and", "prawn?"],
          a: "What is the difference between shrimp and prawn?",
          tip: "WHAT IS = question form for asking about differences.",
          successTip: "USA = 'shrimp' for all sizes 🦐. UK and Australia = 'prawn' for the larger ones. Same animal, different regional names!"
        },
        {
          type: "match",
          q: "Match the seafood animals:",
          pairs: [
            { left: "Crab", right: "Hard shell and two big claws" },
            { left: "Octopus", right: "Eight arms, very intelligent" },
            { left: "Shrimp", right: "Small pink seafood" }
          ],
          a: "completed",
          successTip: "Seafood vocabulary is very useful in restaurants and markets worldwide 🦀🐙🦐!"
        },
        {
          type: "input",
          q: "An animal with eight arms that lives in the ocean and can change color is an ___.",
          a: "octopus",
          alts: ["Octopus","squid"],
          successTip: "Octopuses can change color and solve puzzles! 🐙 One of the most intelligent sea creatures."
        },
        {
          type: "mc",
          q: "Leo sees a sea animal in the tank. It has a hard shell and two big claws. It is a ___.",
          options: ["crab", "fish", "dolphin"],
          a: "crab",
          successTip: "Crabs are eaten worldwide 🦀. Common in seafood restaurants everywhere!"
        },
        {
          type: "mc",
          q: "At the souvenir shop, Maya sees a recipe book. The small pink seafood in American English is called ___.",
          options: ["shrimp", "prawn", "crab"],
          a: "shrimp",
          tip: "REGIONAL DIFFERENCE! USA = shrimp. UK and Australia = prawn.",
          successTip: "Knowing regional vocabulary differences is a mark of a fluent English speaker! 🌎"
        },
        {
          type: "order",
          q: "Translate: El delfin es un animal muy inteligente.",
          words: ["The", "dolphin", "is", "a", "very", "intelligent", "animal."],
          a: "The dolphin is a very intelligent animal.",
          successTip: "Adjective before noun: intelligent animal (not animal intelligent) 📝. Essential English word order!"
        }
      ]
    },

    // ===================================================================
    // SET 6: INSECTS & SMALL CREATURES (A2+)
    // ===================================================================
    {
      id: "m6-u1-s6",
      title: "Set 6: Insects & Small Creatures",
      context: `*Maya's backyard — Friday evening*

Maya's wildlife club assignment: observe small creatures in your garden for one hour.
Maya sits outside with her notebook. She sees bees flying around the flowers, a butterfly landing on a rose, and a spider building a web in the corner.
Later, a fly lands on her notebook. She waits patiently.
As the sun goes down, she sees something magical: tiny creatures with wings that produce a soft yellow-green light, flashing in the bushes.
"Fireflies!" she whispers. She had read about them, but this is the first time she sees them.
She quickly draws a firefly in her notebook.

*El jardín de Maya — viernes por la tarde*

Tarea del club: observar criaturas pequeñas en tu jardín durante una hora.
Maya se sienta afuera con su cuaderno. Ve abejas volando alrededor de las flores, una mariposa posándose en una rosa y una araña construyendo una telaraña en el rincón.
Más tarde, una mosca aterriza en su cuaderno.
Al ponerse el sol, ve algo mágico: pequeñas criaturas con alas que producen una luz amarillo-verdosa, parpadeando entre los arbustos.
"¡Luciérnagas!", susurra. Había leído sobre ellas, pero es la primera vez que las ve.
Rápidamente dibuja una luciérnaga en su cuaderno.`,

      questions: [
        {
          type: "match",
          q: "Match the insects Maya sees in her garden:",
          pairs: [
            { left: "Bee", right: "Makes honey, pollinates flowers" },
            { left: "Butterfly", right: "Beautiful colorful wings" },
            { left: "Spider", right: "Makes webs, has 8 legs" }
          ],
          a: "completed",
          successTip: "Insects represent 75% of all animal species 🐝🦋. Essential for healthy ecosystems!"
        },
        {
          type: "mc",
          q: "Maya sees an insect flying around flowers that makes honey. It is a ___.",
          options: ["bee", "fly", "butterfly"],
          a: "bee",
          successTip: "Bees pollinate crops worldwide 🐝. Without bees, much of our food would disappear. Essential insects!"
        },
        {
          type: "input",
          q: "An insect with large, beautiful, colorful wings that rests on flowers is a ___.",
          a: "butterfly",
          alts: ["Butterfly"],
          successTip: "Over 20,000 butterfly species worldwide 🦋. Found on every continent except Antarctica!"
        },
        {
          type: "mc",
          q: "Maya sees a small creature with EIGHT legs building a web in the corner. It is a ___.",
          options: ["spider", "fly", "bee"],
          a: "spider",
          tip: "SPIDER = 8 legs. It is NOT an insect! Insects have only 6 legs.",
          successTip: "Spiders are arachnids, not insects 🕷️. They have 8 legs. Insects have 6! Important science difference."
        },
        {
          type: "order",
          q: "Translate: Las abejas son importantes para el medio ambiente.",
          words: ["Bees", "are", "important", "for", "the", "environment."],
          a: "Bees are important for the environment.",
          successTip: "Talking about nature and the environment is very common globally 🌍. Important vocabulary for documentaries and conversations!"
        },
        {
          type: "input",
          q: "A small black insect with wings that flies around food and bothers people is a ___.",
          a: "fly",
          alts: ["Fly","mosquito","gnat","maggot"],
          successTip: "There are over 120,000 species of flies 🪰. Very common in warm weather everywhere!"
        },
        {
          type: "mc",
          q: "Maya sees a small red and black creature walking in a line near the flowers. It lives in a colony. It is an ___.",
          options: ["ant", "fly", "butterfly"],
          a: "ant",
          successTip: "Ants are incredibly strong 🐜. They can carry 50 times their own body weight!"
        },
        {
          type: "match",
          q: "Match the small creatures Maya wrote in her notebook:",
          pairs: [
            { left: "Ant", right: "Lives in colonies, incredibly strong" },
            { left: "Fly", right: "Flies around food, annoying" },
            { left: "Mosquito", right: "Bites people, sucks blood" }
          ],
          a: "completed",
          successTip: "The mosquito is considered the most dangerous animal to humans 🦟. They spread diseases like malaria worldwide!"
        },
        {
          type: "input",
          q: "Maya sees the magical creatures in the garden — small insects with wings that produce a yellow-green light in the dark. They are ___.",
          a: "fireflies",
          alts: ["Fireflies", "firefly", "Firefly", "lightning bugs", "Lightning bugs"],
          tip: "These insects produce their own light! They are also called 'lightning bugs' in the USA.",
          successTip: "Fireflies are also called 'lightning bugs' in the USA ✨. They produce light through a chemical reaction. Magical summer insects!"
        },
        {
          type: "mc",
          q: "A small insect that bites people and leaves a red, itchy mark is a ___.",
          options: ["mosquito", "butterfly", "bee"],
          a: "mosquito",
          successTip: "Mosquitoes are the deadliest animals to humans 🦟. They spread malaria, dengue, and other diseases worldwide."
        }
      ]
    },

    // ===================================================================
    // SET 7: WILD ANIMALS — ZOO (B1-)
    // ===================================================================
    {
      id: "m6-u1-s7",
      title: "Set 7: Wild Animals",
      context: `*City Zoo — Saturday morning*

Maya's wildlife club visits the zoo for a school project.
Each student must describe three animals using complete sentences.
Maya takes notes in front of each enclosure.
"The elephant is the largest land animal," she writes. "It uses its long trunk to eat, drink, and communicate."
In front of the big cats area, she sees a lion with a large mane, a tiger with orange and black stripes, and a leopard with spots.
"These animals are all endangered," says the zookeeper. "We work hard to protect them."

*Zoológico de la ciudad — sábado por la mañana*

El club de naturaleza de Maya visita el zoológico para un proyecto escolar.
Cada estudiante debe describir tres animales usando oraciones completas.
Maya toma notas frente a cada recinto.
"El elefante es el animal terrestre más grande", escribe. "Usa su larga trompa para comer, beber y comunicarse."
Frente al área de los grandes felinos, ve un león con melena, un tigre con rayas naranjas y negras, y un leopardo con manchas.
"Estos animales están todos en peligro de extinción", dice el cuidador. "Trabajamos duro para protegerlos."`,

      questions: [
        {
          type: "match",
          q: "Match the wild animals Maya sees at the zoo:",
          pairs: [
            { left: "Lion", right: "King of the savanna, has a mane" },
            { left: "Elephant", right: "Largest land animal, has a trunk" },
            { left: "Giraffe", right: "Tallest animal, very long neck" }
          ],
          a: "completed",
          successTip: "Safari and zoo vocabulary is very common in travel conversations 🦁🐘. Africa, India, and Australia have famous safaris!"
        },
        {
          type: "mc",
          q: "The zookeeper says: 'The ___ is the largest land animal on Earth. It uses its trunk to eat and drink.'",
          options: ["elephant", "lion", "bear"],
          a: "elephant",
          successTip: "Elephants are highly intelligent 🐘. They have excellent memories and live in family groups!"
        },
        {
          type: "input",
          q: "Maya sees the tallest animal at the zoo. It has a very long neck and eats leaves from tall trees. It is a ___.",
          a: "giraffe",
          alts: ["Giraffe"],
          successTip: "Giraffes are the tallest animals on Earth 🦒. They can grow up to 5.5 meters tall!"
        },
        {
          type: "mc",
          q: "In the big cats area, Maya sees a large cat with orange fur and black stripes. It is a ___.",
          options: ["tiger", "lion", "leopard"],
          a: "tiger",
          successTip: "Tigers are the largest cat species 🐯. Found in Asia: India, China, and Russia!"
        },
        {
          type: "order",
          q: "Translate: Los leones viven en Africa.",
          words: ["Lions", "live", "in", "Africa."],
          a: "Lions live in Africa.",
          tip: "PLURAL subject: Lions (plural) live... Use plural verb 'live' for general facts.",
          successTip: "Lions live in African savannas 🦁. A group of lions is called a pride!"
        },
        {
          type: "input",
          q: "A large powerful animal with brown or black fur that lives in forests and mountains. It can stand on two legs. It is a ___.",
          a: "bear",
          alts: ["Bear"],
          successTip: "Bears are found in North America, Europe, and Asia 🐻. Types include grizzly, polar, and black bears!"
        },
        {
          type: "mc",
          q: "Maya sees an animal that looks like a horse but has black and white stripes. It is from Africa. It is a ___.",
          options: ["zebra", "horse", "giraffe"],
          a: "zebra",
          successTip: "No two zebras have the same stripe pattern 🦓 — just like human fingerprints!"
        },
        {
          type: "match",
          q: "Match the big cats to their key features:",
          pairs: [
            { left: "Lion", right: "Has a mane (male only)" },
            { left: "Tiger", right: "Has orange and black stripes" },
            { left: "Leopard", right: "Has spots" }
          ],
          a: "completed",
          successTip: "All big cats are endangered 😢. Conservation is one of the most important global issues today!"
        },
        {
          type: "input",
          q: "The largest land animal at the zoo. It has a long nose called a trunk. It is an ___.",
          a: "elephant",
          alts: ["Elephant"],
          successTip: "Elephants use their trunks to breathe, drink, eat, and even hug each other 🐘!"
        },
        {
          type: "mc",
          q: "Maya reads her notes: 'A gorilla is a large ___. It shares 98% of its DNA with humans.'",
          options: ["ape", "monkey", "bear"],
          a: "ape",
          tip: "IMPORTANT! Gorillas are APES, not monkeys. Apes have no tail. Monkeys have tails.",
          successTip: "Gorilla = ape, not monkey 🦍. Apes: gorillas, chimpanzees, orangutans. Monkeys: smaller animals, have tails. Important difference!"
        }
      ]
    },

    // ===================================================================
    // SET 8: ANIMALS AROUND THE WORLD (B1-)
    // ===================================================================
    {
      id: "m6-u1-s8",
      title: "Set 8: Animals Around the World",
      context: `*Maya's geography class — Tuesday*

Mr. Ellis has a new activity: 'Animals Around the World'.
Each student picks a country and presents its most famous animal.
Maya chooses Australia. Leo chooses Canada. Another student picks China.
"Every region has unique wildlife," says Mr. Ellis. "Knowing regional animals helps you connect with people from different countries."
Maya reads her notes: "Australia: kangaroo, koala, platypus. Canada: moose, beaver, polar bear. China: giant panda."
"My uncle in Canada says he sometimes sees moose near his house!" says Maya.

*La clase de geografía de Maya — martes*

El Sr. Ellis tiene una nueva actividad: 'Animales del mundo'.
Cada estudiante elige un país y presenta su animal más famoso.
Maya elige Australia. Leo elige Canadá. Otro estudiante elige China.
"Cada región tiene fauna única", dice el Sr. Ellis. "Conocer los animales regionales te ayuda a conectar con personas de otros países."
Maya lee sus notas: "Australia: canguro, koala, ornitorrinco. Canadá: alce, castor, oso polar. China: panda gigante."
"¡Mi tío en Canadá dice que a veces ve alces cerca de su casa!", dice Maya.`,

      questions: [
        {
          type: "match",
          q: "Match the animal to its region:",
          pairs: [
            { left: "Kangaroo", right: "Australia" },
            { left: "Moose", right: "Canada and northern USA" },
            { left: "Giant Panda", right: "China" }
          ],
          a: "completed",
          successTip: "Every region has unique wildlife 🌍. Knowing these animals helps you connect with people from different countries!"
        },
        {
          type: "mc",
          q: "Maya presents: 'It jumps and carries babies in a pouch. It lives only in Australia.' It is a ___.",
          options: ["kangaroo", "rabbit", "deer"],
          a: "kangaroo",
          successTip: "The kangaroo is a symbol of Australia 🦘. There are over 50 million kangaroos in Australia!"
        },
        {
          type: "input",
          q: "Leo presents: 'It is the largest member of the deer family. It has very large antlers and lives in Canadian forests.' It is a ___.",
          a: "moose",
          alts: ["Moose"],
          tip: "This animal is called 'moose' in North America. It is much larger than a regular deer.",
          successTip: "The moose is the largest deer species 🫎. It is a symbol of Canada and the northern USA!"
        },
        {
          type: "mc",
          q: "A student presents: 'It has one or two humps and lives in deserts. It can go weeks without water.' It is a ___.",
          options: ["camel", "horse", "cow"],
          a: "camel",
          successTip: "Camels are perfectly adapted to desert life 🐫. They store fat in their humps, not water!"
        },
        {
          type: "order",
          q: "Translate: Los canguros solo viven en Australia.",
          words: ["Kangaroos", "only", "live", "in", "Australia."],
          a: "Kangaroos only live in Australia.",
          tip: "ONLY position: Subject + only + verb + place.",
          successTip: "Australia has unique wildlife found nowhere else on Earth 🦘🐨. Kangaroos, koalas, and platypus are only there!"
        },
        {
          type: "input",
          q: "A grey animal that sleeps up to 22 hours a day and eats eucalyptus leaves. Found only in Australia. It is a ___.",
          a: "koala",
          alts: ["Koala"],
          successTip: "Koalas sleep so much because eucalyptus leaves have very few nutrients 🐨. Found ONLY in Australia!"
        },
        {
          type: "mc",
          q: "Mr. Ellis asks: 'Which large grey animal has a horn on its nose and is critically endangered?'",
          options: ["rhinoceros", "elephant", "hippopotamus"],
          a: "rhinoceros",
          tip: "Also called 'rhino' for short!",
          successTip: "Rhinoceros (rhino) are critically endangered 🦏. Found in Africa and Asia. Their horn is made of keratin — the same material as your fingernails!"
        },
        {
          type: "match",
          q: "Match the animals to their continents:",
          pairs: [
            { left: "Giant Panda", right: "Asia (China)" },
            { left: "Bison", right: "North America" },
            { left: "Llama", right: "South America" }
          ],
          a: "completed",
          successTip: "Each continent has its own unique species 🌎. Great geography AND nature vocabulary together!"
        },
        {
          type: "input",
          q: "A black and white animal that eats bamboo and lives in the mountains of China. It is a ___.",
          a: "panda",
          alts: ["Panda", "giant panda", "Giant panda"],
          successTip: "The giant panda is a symbol of China 🐼. Only about 1,800 remain in the wild!"
        },
        {
          type: "mc",
          q: "Mr. Ellis explains: 'Rabbits and hares look similar, but hares ___ bigger and faster than rabbits.'",
          options: ["are", "is", "be"],
          a: "are",
          tip: "PLURAL subject: Hares (plural) → ARE (not 'is'!).",
          successTip: "Rabbit = smaller, social, lives in groups 🐰. Hare = bigger, faster, solitary 🐇. Another animal difference mastered!"
        }
      ]
    },

    // ===================================================================
    // SET 9: COMMON MISTAKES (B1)
    // ===================================================================
    {
      id: "m6-u1-s9",
      title: "Set 9: Common Mistakes",
      context: `*Maya's bedroom — Sunday night*

Maya is reviewing her animal vocabulary notes before the wildlife club quiz tomorrow.
"I need to be careful with adjective order," she says to herself.
She reads a tip from her textbook: "In English, adjectives go BEFORE the noun. When you use two adjectives, the opinion word comes before the size word: a beautiful big elephant, not a big beautiful elephant."
She also reviews irregular plurals and animal pronouns.
Leo sends a message: "Help! Which is correct: 'I saw a beautiful big cat' or 'I saw a big beautiful cat'?"
Maya smiles and types her answer.

*La habitación de Maya — domingo por la noche*

Maya está repasando sus apuntes de vocabulario antes del concurso de mañana.
"Tengo que tener cuidado con el orden de los adjetivos", se dice.
Lee un consejo de su libro: "En inglés, los adjetivos van ANTES del sustantivo. Cuando usas dos adjetivos, la palabra de opinión va antes que la de tamaño: a beautiful big elephant, no a big beautiful elephant."
También repasa los plurales irregulares y los pronombres de animales.
Leo le envía un mensaje: "¡Ayuda! ¿Cuál es correcto: 'I saw a beautiful big cat' o 'I saw a big beautiful cat'?"
Maya sonríe y escribe su respuesta.`,

      questions: [
        {
          type: "mc",
          q: "Which sentence is CORRECT?",
          options: ["I have a dog.", "I have a perro.", "I have dog."],
          a: "I have a dog.",
          tip: "Never mix Spanish and English! Always use 'a/an' with singular animals.",
          successTip: "Always use English animal names in English 🐕. And always use 'a/an' with singular countable nouns!"
        },
        {
          type: "mc",
          q: "Leo asks Maya: 'Which word order is correct in English?'",
          options: ["I saw a big rat.", "I saw a rat big.", "I saw rat big."],
          a: "I saw a big rat.",
          tip: "ADJECTIVE ORDER: Adjective + Noun. Big rat (NOT rat big). Also use 'a' before the noun!",
          successTip: "English = adjective BEFORE noun 📝. Spanish = adjective AFTER noun (rata grande). Opposite word order!"
        },
        {
          type: "order",
          q: "Translate: Vi un gato negro.",
          words: ["I", "saw", "a", "black", "cat."],
          a: "I saw a black cat.",
          tip: "ADJECTIVE FIRST: black cat (NOT cat black).",
          successTip: "English = adjective + noun: black cat 🐈‍⬛. Spanish = noun + adjective: gato negro. Opposite order!"
        },
        {
          type: "mc",
          q: "Complete: 'The ___ is female. The ___ is male.' (for cattle on a farm)",
          options: ["cow / bull", "bull / cow", "cow / cow"],
          a: "cow / bull",
          tip: "COW = female 🐄, BULL = male 🐂. Don't confuse them!",
          successTip: "Cow = female, Bull = male. Very important for farming vocabulary and general English!"
        },
        {
          type: "input",
          q: "Complete CORRECTLY: I have ___ beautiful dog.",
          a: "a",
          alts: ["A","one"],
          tip: "USE 'A'! I have A beautiful dog. Never forget the article before a singular noun!",
          successTip: "Always use 'a/an' with singular countable nouns 📝. Very common Spanish-speaker mistake!"
        },
        {
          type: "mc",
          q: "Which sentence is CORRECT?",
          options: ["There are five sheep.", "There are five sheeps.", "There is five sheep."],
          a: "There are five sheep.",
          tip: "Two rules: 1. Sheep = no -S plural. 2. THERE ARE for more than one.",
          successTip: "Double grammar check! 1. Sheep = no -S 🐑. 2. There ARE (plural). Two mistakes fixed at once!"
        },
        {
          type: "order",
          q: "Translate: Vi tres ratones grandes.",
          words: ["I", "saw", "three", "big", "mice."],
          a: "I saw three big mice.",
          tip: "Two things to remember: 1. Mice (NOT mouses). 2. Big mice (NOT mice big).",
          successTip: "Irregular plural + adjective order together 🐭. You are mastering tricky English!"
        },
        {
          type: "input",
          q: "Complete: I love my cat. ___ is very friendly.",
          a: "It",
          alts: ["it", "He", "he", "She", "she"],
          tip: "Animals = IT (neutral) unless you know the specific gender.",
          successTip: "Animals = 'it' in English 🐱. If you know it is female, 'she' is fine. If male, 'he' is fine. Otherwise, use 'it'!"
        },
        {
          type: "mc",
          q: "Which sentence is WRONG?",
          options: ["I saw two deers.", "I saw two deer.", "I saw two fish."],
          a: "I saw two deers.",
          tip: "DEER = no -S! Two deer (NOT deers). Just like sheep and fish!",
          successTip: "Finding errors is an important skill ❌. Deer = same in singular and plural. No -S ever!"
        },
        {
          type: "order",
          q: "Translate: Los elefantes son animales inteligentes.",
          words: ["Elephants", "are", "intelligent", "animals."],
          a: "Elephants are intelligent animals.",
          tip: "ADJECTIVE + NOUN: intelligent animals (NOT animals intelligent).",
          successTip: "Adjective always before the noun in English 🐘. Essential for all descriptions!"
        }
      ]
    },

    // ===================================================================
    // SET 10: MASTERY CHALLENGE (B1)
    // ===================================================================
    {
      id: "m6-u1-s10",
      title: "Set 10: Mastery Challenge",
      context: `*Wildlife Club quiz — Monday afternoon*

The wildlife club quiz begins. Maya and Leo sit at the same table.
The quizmaster, Mr. Ellis, reads the questions.
"Some questions test facts. Some test grammar. Some test both!" he says.
Maya feels confident. She has practiced all week.
"Remember," says Mr. Ellis, "when you use two adjectives in English, the opinion adjective comes BEFORE the size adjective.
For example: 'a beautiful big elephant', not 'a big beautiful elephant'."
Maya writes the rule in her notebook and smiles. She is ready.

*Concurso del club de naturaleza — lunes por la tarde*

Comienza el concurso. Maya y Leo se sientan en la misma mesa.
"Algunas preguntas ponen a prueba los hechos. ¡Otras la gramática. Y otras ambas!", dice el Sr. Ellis.
Maya se siente segura. Ha practicado toda la semana.
"Recuerden", dice el Sr. Ellis, "cuando usan dos adjetivos en inglés, el adjetivo de opinión va ANTES que el adjetivo de tamaño.
Por ejemplo: 'a beautiful big elephant', no 'a big beautiful elephant'."
Maya escribe la regla y sonríe. Está lista.`,

      questions: [
        {
          type: "match",
          q: "MASTERY: Match the animal superlatives:",
          pairs: [
            { left: "Largest animal ever", right: "Blue whale" },
            { left: "Tallest animal", right: "Giraffe" },
            { left: "Fastest land animal", right: "Cheetah" }
          ],
          a: "completed",
          successTip: "Knowing animal superlatives makes great conversation 🦒🐋. Very common in documentaries worldwide!"
        },
        {
          type: "mc",
          q: "Complete both: One mouse → two ___. One goose → two ___.",
          options: ["mice / geese", "mouses / gooses", "mice / gooses"],
          a: "mice / geese",
          tip: "BOTH IRREGULAR! Mouse → Mice. Goose → Geese.",
          successTip: "You learned the hardest irregular plurals! 🎓 Mouse → Mice, Goose → Geese. Most learners struggle with these!"
        },
        {
          type: "input",
          q: "The only bird in the world that can fly backwards. It is very small and its wings move incredibly fast. It is a ___.",
          a: "hummingbird",
          alts: ["Hummingbird", "humming bird", "Humming bird"],
          tip: "This tiny bird is found in the Americas. Its name tells you what it does!",
          successTip: "Hummingbirds are the only birds that can fly backwards 🐦. Found in the Americas, especially Canada and the USA!"
        },
        {
          type: "order",
          q: "Translate: Pez en plural tambien es pez.",
          words: ["Fish", "in", "plural", "is", "also", "fish."],
          a: "Fish in plural is also fish.",
          tip: "SAME FORM! One fish, two fish. No -S needed!",
          successTip: "You can now explain English grammar rules in English 🎓. That is an advanced skill!"
        },
        {
          type: "mc",
          q: "Quiz question: 'A sea turtle lives ___ the ocean. A land tortoise lives ___ land.'",
          options: ["in / on", "on / in", "in / in"],
          a: "in / on",
          tip: "IN the ocean (water). ON land (surface). Different prepositions!",
          successTip: "Preposition precision: IN the ocean 🌊, ON land 🏜️. Mastering small words makes a big difference!"
        },
        {
          type: "match",
          q: "MASTERY: Match the animal differences:",
          pairs: [
            { left: "Turtle / Tortoise", right: "Water vs Land" },
            { left: "Frog / Toad", right: "Smooth vs Rough skin" },
            { left: "Rabbit / Hare", right: "Small and social vs Large and fast" }
          ],
          a: "completed",
          successTip: "All three animal differences mastered 🎓. Turtle/tortoise, frog/toad, rabbit/hare. Native-level vocabulary knowledge!"
        },
        {
          type: "input",
          q: "Name one animal that has the SAME form in singular and plural.",
          a: "sheep",
          alts: ["Sheep", "deer", "Deer", "fish", "Fish", "moose", "Moose"],
          tip: "Multiple correct answers! Think of the animals from Set 2.",
          successTip: "Sheep, deer, fish, and moose all have the same singular and plural form 🐑🦌🐟🫎. Irregular plurals mastered!"
        },
        {
          type: "mc",
          q: "At a seafood restaurant in New York (USA), you want small pink seafood. You order ___.",
          options: ["shrimp", "oyster", "chicken"],
          a: "shrimp",
          tip: "USA = shrimp. UK and Australia = prawn.",
          successTip: "You can now order seafood correctly in any English-speaking country 🦐. Practical vocabulary!"
        },
        {
          type: "order",
          q: "Translate: Los pandas estan en peligro de extincion.",
          words: ["Pandas", "are", "endangered", "animals."],
          a: "Pandas are endangered animals.",
          successTip: "Endangered = in danger of extinction 🐼. Important environmental vocabulary used worldwide!"
        },
        {
          type: "mc",
          q: "FINAL MASTERY — Mr. Ellis asks: 'Which sentence uses perfect English adjective order?'",
          options: [
            "I saw three beautiful big elephants in Africa.",
            "I saw three big beautiful elephants in Africa.",
            "I saw elephants three big beautiful in Africa."
          ],
          a: "I saw three beautiful big elephants in Africa.",
          tip: "ADJECTIVE ORDER: Number → Opinion → Size → Noun. Beautiful (opinion) comes BEFORE big (size)!",
          successTip: "Three + beautiful (opinion) + big (size) + elephants 🐘. Opinion before size — the key rule Mr. Ellis explained at the start of this set!"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET
  // ===================================================================
 readingSet: {
  id: "m6-u1-reading",
  title: "Reading Practice: A Day at the Zoo",
  category: "reading",
  passages: [

    // ─── PASSAGE 1 ───────────────────────────────────────────────────
    {
      id: "m6-u1-reading-p1",
      text: `My Amazing Zoo Visit

Last Saturday, my family and I visited the National Zoo. It was an incredible experience! We saw so many different animals from around the world.

First, we went to see the elephants. They are the largest land animals on Earth! One elephant was using its long trunk to spray water on itself. My little sister loved watching them. She said that elephants are very intelligent animals.`,

      questions: [
        {
          type: "mc",
          q: "When did the family visit the zoo?",
          options: ["Last Saturday", "Last Sunday", "Yesterday"],
          a: "Last Saturday"
        },
        {
          type: "mc",
          q: "What are the largest land animals on Earth, according to the text?",
          options: ["Elephants", "Lions", "Giraffes"],
          a: "Elephants",
          successTip: "Elephants = largest land animals 🐘. Reading reinforced your vocabulary!"
        }
      ]
    },

    // ─── PASSAGE 2 ───────────────────────────────────────────────────
    {
      id: "m6-u1-reading-p2",
      text: `After that, we walked to the African Safari area. There we saw lions, tigers, and giraffes. The lions were sleeping under a tree. The zookeeper told us that lions sleep for about 20 hours a day! The giraffes were eating leaves from tall trees. They are the tallest animals in the world.

Next, we visited the reptile house. We saw snakes, lizards, and even a huge crocodile! The most interesting thing I learned was the difference between turtles and tortoises. The zookeeper explained that turtles live in water and tortoises live on land. There was also a small green frog. The guide told us that frogs have smooth skin, but toads have dry, rough skin.`,

      questions: [
        {
          type: "input",
          q: "How many hours a day do lions sleep, according to the zookeeper?",
          a: "20",
          alts: ["twenty", "Twenty", "about 20", "20 hours", "twenty hours"],
          tip: "Find this number in the first paragraph of this passage."
        },
        {
          type: "mc",
          q: "What is the difference between turtles and tortoises, according to the text?",
          options: [
            "Turtles live in water, tortoises on land.",
            "Tortoises live in water, turtles on land.",
            "They are the same animal."
          ],
          a: "Turtles live in water, tortoises on land.",
          successTip: "Turtle = water 🐢🌊. Tortoise = land 🐢🏜️. Reading reinforced your learning!"
        },
        {
          type: "mc",
          q: "What is the difference between frog skin and toad skin, according to the guide?",
          options: [
            "Frogs have smooth skin, toads have rough skin.",
            "Toads have smooth skin, frogs have rough skin.",
            "Both have the same skin."
          ],
          a: "Frogs have smooth skin, toads have rough skin.",
          successTip: "Frog = smooth skin 🐸. Toad = rough, dry skin. Important animal difference!"
        }
      ]
    },

    // ─── PASSAGE 3 ───────────────────────────────────────────────────
    {
      id: "m6-u1-reading-p3",
      text: `In the ocean exhibit, I saw dolphins jumping and playing in a large tank. Dolphins are very intelligent and friendly animals. We also saw sharks swimming slowly. The guide told us that most sharks are not dangerous to humans. There were also colorful fish, octopuses with eight arms, and crabs with big claws.`,

      questions: [
        {
          type: "mc",
          q: "Are most sharks dangerous to humans, according to the guide?",
          options: [
            "No, most sharks are not dangerous.",
            "Yes, all sharks are very dangerous.",
            "Only the large ones are dangerous."
          ],
          a: "No, most sharks are not dangerous.",
          successTip: "Most sharks are harmless 🦈. Important to correct common misconceptions!"
        },
        {
          type: "mc",
          q: "How many arms does an octopus have, according to the text?",
          options: ["Eight", "Six", "Ten"],
          a: "Eight",
          successTip: "Octo = 8 in Latin 🐙. That is why the word 'octopus' starts with 'octo'!"
        }
      ]
    },

    // ─── PASSAGE 4 ───────────────────────────────────────────────────
    {
      id: "m6-u1-reading-p4",
      text: `Before we left, we visited the bird area. We saw eagles, parrots, and geese. My brother learned that the plural of goose is geese, not gooses! One colorful parrot said "Hello!" to us.

It was an amazing day. I want to visit more zoos and maybe go on a safari in Africa one day!`,

      questions: [
        {
          type: "input",
          q: "What is the plural of 'goose', according to the text?",
          a: "geese",
          alts: ["Geese"],
          successTip: "The story taught grammar naturally 📖. Goose → Geese — irregular plural!"
        },
        {
          type: "mc",
          q: "What did one of the parrots do when the family visited the bird area?",
          options: ["It said 'Hello!'", "It flew away.", "It ate food from their hands."],
          a: "It said 'Hello!'"
        },
        {
          type: "mc",
          q: "What does the writer want to do in the future?",
          options: [
            "Visit more zoos and go on safari.",
            "Work at a zoo.",
            "Study to be a zookeeper."
          ],
          a: "Visit more zoos and go on safari.",
          successTip: "Unit 1 Reading Complete! 🎉 You understood a full story about animals 📖. Your vocabulary is growing fast!"
      }
      ]
    }

  ]
}
};
